import LegalLayout from "./LegalLayout";

export default function SMSTerms() {
  return (
    <LegalLayout title="SMS Terms & Conditions" lastUpdated="February 2026">
      <Section title="Program Description">
        <p>
          PROX by Shadow Bridge Technologies, Inc. is a privacy-focused messaging relay service.
          When you sign up for PROX, you receive a dedicated proxy phone number. All SMS and MMS
          messages sent to and from your proxy number are privately forwarded through our Shadow
          Mapping system, keeping your real phone number hidden from your contacts.
        </p>
      </Section>

      <Section title="Opt-In">
        <p>
          By signing up for PROX and providing your phone number, you expressly consent to receive
          SMS and MMS messages from and through the PROX service, including:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>Forwarded messages from your contacts via assigned Shadow Numbers.</li>
          <li>Service notifications (mapping confirmations, burn confirmations, vCard deliveries).</li>
          <li>Responses to your commands (HELP, SAVE, BURN, RESET).</li>
        </ul>
        <p>
          Consent is not a condition of purchase. You may opt out at any time.
        </p>
      </Section>

      <Section title="Message Frequency">
        <p>
          Message frequency varies based on your usage. Each incoming message from a contact will
          generate one forwarded message to your phone. Service notifications (vCards, command
          confirmations) are sent as needed. There is no fixed message limit.
        </p>
      </Section>

      <Section title="Message and Data Rates">
        <p>
          <strong className="text-white">Message and data rates may apply.</strong> Standard carrier
          messaging rates from your mobile provider apply to all messages sent and received through
          PROX. Check with your carrier for details about your messaging plan.
        </p>
      </Section>

      <Section title="Opt-Out">
        <p>
          You can opt out of PROX messaging at any time:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>
            <strong className="text-zinc-200">As a PROX user:</strong> Contact us at{" "}
            <a href="mailto:support@prox.day" className="text-white underline underline-offset-2">
              support@prox.day
            </a>{" "}
            to deactivate your account and stop all message forwarding.
          </li>
          <li>
            <strong className="text-zinc-200">As a contact/stranger:</strong> Reply{" "}
            <span className="font-mono text-white bg-zinc-800 px-1.5 py-0.5 rounded text-xs">STOP</span>{" "}
            to any PROX number to opt out of receiving messages from that proxy. You will receive a
            confirmation and no further messages will be sent from that number.
          </li>
        </ul>
        <p>
          After opting out, reply{" "}
          <span className="font-mono text-white bg-zinc-800 px-1.5 py-0.5 rounded text-xs">START</span>{" "}
          to re-subscribe.
        </p>
      </Section>

      <Section title="Help">
        <p>
          For assistance, reply{" "}
          <span className="font-mono text-white bg-zinc-800 px-1.5 py-0.5 rounded text-xs">HELP</span>{" "}
          to any PROX number, or contact us:
        </p>
        <ul className="list-disc pl-6 space-y-2 text-zinc-400">
          <li>
            Email:{" "}
            <a href="mailto:support@prox.day" className="text-white underline underline-offset-2">
              support@prox.day
            </a>
          </li>
          <li>
            Website:{" "}
            <a href="https://prox.day" className="text-white underline underline-offset-2">
              prox.day
            </a>
          </li>
        </ul>
      </Section>

      <Section title="Supported Carriers">
        <p>
          PROX messaging is compatible with all major U.S. carriers including AT&T, T-Mobile,
          Verizon, and others. Carrier support for MMS (picture messages, vCards) may vary.
        </p>
      </Section>

      <Section title="Privacy">
        <p>
          We handle your phone number and messaging data in accordance with our{" "}
          <a href="/privacy" className="text-white underline underline-offset-2">Privacy Policy</a>.
          PROX does not store message content after delivery. We do not sell your personal information.
        </p>
      </Section>

      <Section title="Service Provider">
        <p>
          Shadow Bridge Technologies, Inc.<br />
          Website:{" "}
          <a href="https://prox.day" className="text-white underline underline-offset-2">
            prox.day
          </a><br />
          Email:{" "}
          <a href="mailto:support@prox.day" className="text-white underline underline-offset-2">
            support@prox.day
          </a>
        </p>
      </Section>
    </LegalLayout>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-heading text-lg text-white mb-3">{title}</h2>
      <div className="space-y-3">{children}</div>
    </section>
  );
}
